import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/runtime';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);
  const [isSignUp, setIsSignUp] = useState(false);

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleError, setGoogleError] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  // Sign up form state
  const [fullName, setFullName] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState('');

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const processLogin = (user) => {
    localStorage.setItem('user', JSON.stringify(user));
    if (onLoginSuccess) onLoginSuccess(user);
    const role = (user?.role || '').toLowerCase();
    if (role === 'system admin') navigate('/admin/dashboard');
    else if (role === 'branch manager') navigate('/manager/dashboard');
    else navigate('/');
    setEmail('');
    setPassword('');
    onClose();
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setGoogleError('');
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('login.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!data.success) { setError(data.message || 'Login failed'); setLoading(false); return; }
      processLogin(data.user);
      setLoading(false);
    } catch (err) {
      setError('Connection error: ' + err.message);
      setLoading(false);
    }
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    setSignUpError('');
    setSignUpSuccess('');
    if (!fullName.trim() || !signUpEmail.trim() || !signUpPassword || !confirmPassword) {
      setSignUpError('Please complete all fields.');
      return;
    }
    if (signUpPassword !== confirmPassword) {
      setSignUpError('Passwords do not match.');
      return;
    }
    setSignUpLoading(true);
    try {
      const response = await fetch(buildApiUrl('register_client.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name: fullName.trim(), email: signUpEmail.trim(), password: signUpPassword }),
      });
      const data = await response.json();
      if (!data.success) { setSignUpError(data.message || 'Failed to create account'); setSignUpLoading(false); return; }
      setSignUpSuccess('Account created. You can log in now.');
      setEmail(signUpEmail.trim());
      setPassword('');
      setFullName('');
      setSignUpEmail('');
      setSignUpPassword('');
      setConfirmPassword('');
      setIsSignUp(false);
    } catch (err) {
      setSignUpError('Connection error: ' + err.message);
    } finally {
      setSignUpLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);
    try {
      const response = await fetch(buildApiUrl('forgot_password.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await response.json();
      if (!data.success) { setForgotError(data.message || 'Failed to process request'); setForgotLoading(false); return; }
      setForgotMessage(data.message || 'If the account exists, a reset email has been sent.');
      setForgotLoading(false);
    } catch (err) {
      setForgotError('Connection error: ' + err.message);
      setForgotLoading(false);
    }
  };

  const handleGoogleCredentialResponse = async (response) => {
    if (!response?.credential) { setGoogleError('Google login failed. Please try again.'); return; }
    setError('');
    setGoogleError('');
    setLoading(true);
    try {
      const apiResponse = await fetch(buildApiUrl('google_login.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await apiResponse.json();
      if (!data.success) { setGoogleError(data.message || 'Google login failed'); setLoading(false); return; }
      processLogin(data.user);
      setLoading(false);
    } catch (err) {
      setGoogleError('Google login error: ' + err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || isSignUp || !GOOGLE_CLIENT_ID || !googleButtonRef.current) return;
    const renderGoogleButton = () => {
      if (!window.google || !googleButtonRef.current) return;
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse,
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        width: 320,
      });
    };
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) { renderGoogleButton(); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    document.body.appendChild(script);
  }, [isOpen, isSignUp]);

  if (!isOpen) return null;

  const inputClass =
    'w-full px-4 py-3 bg-gray-100 text-gray-800 placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm sm:text-base';

  const AlertBox = ({ type, text }) => (
    <div className={`p-3 rounded-lg text-sm border ${type === 'error'
        ? 'bg-red-100 border-red-400 text-red-700'
        : 'bg-green-100 border-green-400 text-green-700'
      }`}>
      {text}
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal container — scrollable on very small screens */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="
            relative bg-white rounded-2xl shadow-2xl
            w-full max-w-sm
            mx-auto my-auto
            p-6 sm:p-8
            animate-fadeIn
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <h2 className="text-2xl sm:text-3xl font-bold text-black mb-6 sm:mb-8">
            {isSignUp ? 'Sign up' : 'Log in'}
          </h2>

          {/* ── Login Form ── */}
          {!isSignUp ? (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              {signUpSuccess && <AlertBox type="success" text={signUpSuccess} />}
              {error && <AlertBox type="error" text={error} />}
              {googleError && <AlertBox type="error" text={googleError} />}

              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l18 18" />
                      <path d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" />
                      <path d="M9.88 5.09A10.94 10.94 0 0112 5c5.05 0 9.27 3.11 10 7-0.29 1.56-1.15 3-2.43 4.17" />
                      <path d="M6.61 6.61C4.62 7.97 3.29 9.86 3 12c0.73 3.89 4.95 7 10 7 1.21 0 2.37-.18 3.46-.5" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-gray-600">Remember Me</span>
                </label>
                <button
                  type="button"
                  onClick={() => { setShowForgotModal(true); setForgotEmail(email); setForgotError(''); setForgotMessage(''); }}
                  className="text-gray-600 hover:text-[#B89F80] transition-colors"
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white py-3 rounded-full font-semibold hover:bg-gray-800 transition-colors mt-4 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {loading ? 'Logging in...' : 'Log in'}
              </button>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Google button — constrained so it never overflows */}
              <div className="flex justify-center overflow-hidden">
                {GOOGLE_CLIENT_ID ? (
                  <div
                    ref={googleButtonRef}
                    className="max-w-full"
                    data-testid="google-login-button"
                  />
                ) : (
                  <p className="text-xs text-red-600 text-center">Set `REACT_APP_GOOGLE_CLIENT_ID` to enable Google login.</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => setIsSignUp(true)}
                className="w-full bg-gray-100 text-gray-800 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors mt-3 text-sm sm:text-base"
              >
                Sign up
              </button>
            </form>
          ) : (
            /* ── Sign Up Form ── */
            <form onSubmit={handleSignUpSubmit} className="space-y-4">
              {signUpError && <AlertBox type="error" text={signUpError} />}

              <input
                type="text"
                placeholder="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={inputClass}
              />
              <input
                type="email"
                placeholder="Email Address"
                value={signUpEmail}
                onChange={(e) => setSignUpEmail(e.target.value)}
                className={inputClass}
              />
              <input
                type="password"
                placeholder="Password"
                value={signUpPassword}
                onChange={(e) => setSignUpPassword(e.target.value)}
                className={inputClass}
              />
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClass}
              />

              <button
                type="submit"
                disabled={signUpLoading}
                className="w-full bg-black text-white py-3 rounded-full font-semibold hover:bg-gray-800 transition-colors mt-4 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {signUpLoading ? 'Creating...' : 'Create Account'}
              </button>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsSignUp(false)}
                className="w-full bg-gray-100 text-gray-800 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors text-sm sm:text-base"
              >
                Log in now
              </button>
            </form>
          )}
        </div>
      </div>

      {/* ── Forgot Password Modal ── */}
      {showForgotModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowForgotModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-auto p-5 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-black mb-2">Forgot Password</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter your email and we will send a temporary password.
            </p>

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-3">
              {forgotError && <AlertBox type="error" text={forgotError} />}
              {forgotMessage && <AlertBox type="success" text={forgotMessage} />}

              <input
                type="email"
                placeholder="Email Address"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className={inputClass}
              />

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowForgotModal(false)}
                  className="flex-1 bg-gray-100 text-gray-800 py-2.5 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1 bg-black text-white py-2.5 rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default LoginModal;
